<template>
  <div class="frb">
    <div class="frb-main">
      <div class="display-flex">
      <FeedReactionButton
        :reaction-type="reacted ? reactionType : null"
        :count="reactionCount"
        :size="16"
        :options="REACTION_OPTIONS"
        @react="handleReact"
      />
      <div v-if="chips.length" class="frb-chips">
      <span
        v-for="chip in chips"
        :key="chip.reactionType"
        class="frb-chip"
        :class="{ 'frb-chip--active': reacted && reactionType === chip.reactionType }"
        role="button"
        tabindex="0"
        @click="handleReact(chip.reactionType)"
        @keydown.enter="handleReact(chip.reactionType)"
        @keydown.space.prevent="handleReact(chip.reactionType)"
      >
        <span class="frb-chip-emoji">{{ reactionEmoji(chip.reactionType) }}</span>
        <span class="frb-chip-count">{{ chip.count }}</span>
    </span>
    </div>
</div>
      <f7-link class="frb-btn" @click="handleComment">
        <f7-icon f7="bubble_left" size="16" />
        <span v-if="commentsCount > 0">{{ commentsCount }}</span>
      </f7-link>

      <f7-link class="frb-btn" @click="handleRepost">
        <f7-icon f7="repeat" size="16" />
        <span v-if="repostsCount > 0">{{ repostsCount }}</span>
      </f7-link>

      <span ref="shareBtn">
        <f7-link class="frb-btn" @click="openShare()">
          <f7-icon f7="square_arrow_up" size="16" />
        </f7-link>
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { f7 } from 'framework7-vue'
import FeedReactionButton from './FeedReactionButton.vue'
import { REACTION_OPTIONS, reactionEmoji } from '../../js/feedReactions.js'
import { useSocialShare } from '../../js/composables/useSocialShare.js'
import { usePostStore } from '../../js/pinia-stores/post.js'
import { useAccountStore } from '../../js/pinia-stores/account.js'
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  reacted:        { type: Boolean, default: false },
  reactionType:   { type: String,  default: 'LIKE' },
  reactionCount:  { type: Number,  default: 0 },
  commentsCount:  { type: Number,  default: 0 },
  repostsCount:   { type: Number,  default: 0 },
  reactions:      { type: Array,   default: () => [] },
  shareContent:   { type: Object,  default: () => ({ subject: '', message: '', url: '', files: [] }) },
  postId:         { type: String,  default: '' },
  itemType:       { type: String,  default: 'post' },
  commentId:      { type: String,  default: '' },
  subCommentId:   { type: String,  default: '' },
})

const emit = defineEmits(['react', 'comment', 'repost', 'shareToChat'])

const shareBtn = ref(null)
const { socialShare } = useSocialShare()

let shareActions = null

const shareButtons = [
  { text: 'Repartager', icon: '<i class="f7-icons">repeat</i>', onClick: onRepost },
  { text: 'Chat groupe', icon: '<img src="/512x512.svg" width="24" height="24" alt="avocash" />', onClick: onGroupChat },
  { text: 'WhatsApp', icon: '<i class="f7-icons" style="color:#25D366">logo_whatsapp</i>', onClick: onWhatsApp },
  { text: 'Autres', icon: '<i class="f7-icons" style="color:#3B4FCA">paperplane</i>', onClick: onOtherApp },
  { text: 'Copier lien', icon: '<i class="f7-icons">link</i>', onClick: onCopyLink },
]

const chips = computed(() => (props.reactions || []).filter((r) => (r.count || 0) > 0))

const postStore = usePostStore()
const accountStore = useAccountStore()

async function handleReact(reactionType) {
  if (!props.postId) {
    emit('react', reactionType)
    return
  }
  const user = accountStore.primaryAccount || accountStore.userDetail
  if (!user) return
  const reactionPayload = {
    reactionType,
    users: [{ userId: user.userId }],
    authors: [{ userId: user.userId, name: user.name || user.username, logo: user.avatar }]
  }
  await postStore.reactToPost({
    postId: props.postId,
    commentId: props.commentId,
    subCommentId: props.subCommentId,
    itemType: props.itemType,
    reaction: reactionPayload,
  })
  emit('react', reactionType)
}

async function handleComment() {
  emit('comment')
}

async function handleRepost() {
  if (!props.postId) {
    emit('repost')
    return
  }
  const payload = {
    postId: props.postId,
    post: { repostedBy: accountStore.primaryAccount?.userId || accountStore.userDetail?.userId }
  }
  await postStore.repostPost(payload)
  emit('repost')
}

function openShare() {
  if (shareActions?.opened) {
    shareActions.close()
    return
  }
  if (!shareActions) {
    shareActions = f7.actions.create({
      convertToPopover: true,
      closeOnEscape: true,
      closeByOutsideClick: true,
      backdrop: false,
      grid: true,
      targetEl: shareBtn.value,
      cssClass: 'frb-share-actions',
      buttons: shareButtons,
    })
  }
  shareActions.open()
}

onBeforeUnmount(() => {
  shareActions?.destroy()
})

function onRepost() {
  emit('repost')
}

function onGroupChat() {
  emit('shareToChat')
}

function onWhatsApp() {
  const text = encodeURIComponent(props.shareContent?.message || props.shareContent?.subject || '')
  window.open(`https://wa.me/?text=${text}`, '_blank')
}

function onOtherApp() {
  socialShare({
    subject: props.shareContent?.subject || '',
    message: props.shareContent?.message || '',
    url: props.shareContent?.url || '',
    files: props.shareContent?.files || [],
  })
}

function onCopyLink() {
  const url = props.shareContent?.url || ''
  navigator.clipboard?.writeText(url)
  f7.toast.show({ text: t('feed.linkCopied'), position: 'center', closeTimeout: 1500 })
}
</script>

<style scoped>
.frb { position: relative; overflow: visible; }
.frb-main { overflow: visible; 
  display: flex;
  justify-content: space-between;
  padding-top: 8px;
  margin-top: 4px;
  border-top: 0.5px solid var(--f7-list-item-border-color, #e0e0e0);
}
.frb-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
  background: none;
  border: none;
  padding: 4px 6px;
  cursor: pointer;
}
.frb-btn--active {
  color: var(--avocash-green, #1A6B42);
  font-weight: 500;
}
.frb-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding-top: 6px;
}
.frb-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  /* background: var(--f7-list-bg-color, #f0f0f0);
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0)); */
  /* border-radius: 12px; */
  padding: 1px 7px;
  font-size: 10px;
  cursor: pointer;
}
.frb-chip--active {
  background: #EAF2EB;
  border-color: var(--avocash-green, #1A6B42);
}
.frb-chip-count { color: var(--f7-on-surface-variant, #8e8e93); }
.frb-chip--active .frb-chip-count { color: var(--avocash-green, #1A6B42); font-weight: 500; }
.frb-share-actions {
  --f7-actions-grid-button-icon-size: 36px;
  --f7-actions-grid-button-font-size: 11px;
}
.frb-share-actions.popover {
  width: auto;
  min-width: 300px;
  --f7-popover-width: auto;
}
</style>
